import React, {FunctionComponent, useEffect, useMemo} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {RouteProps} from "react-router-dom";
import useSearchFilter from "../../../hooks/useSearchFilter";
import FilterGroup from "../filters/FilterGroup";
import FormAutocompleteSelectPageable from "../input/FormAutocompleteSelectPageable";
import {WorkflowStatesEnum} from "../../../constants/workflow/WorkflowStates";
import {tourService} from "../../../services/TourService";
import Button from "../../atoms/Button";
import {ColorType} from "../../../types/bootstrap/BootstrapType";
import usePaginatedData from "../../../hooks/usePaginatedData";
import Pagination from "../pagination/Pagination";
import DateFilter from "../filters/DateFilter";
import {customerService} from "../../../services/CustomerService";
import {OrderSearchObject, OrderState} from "../../../interfaces/OrderInterfaces";
import {orderService} from "../../../services/OrderService";
import OrderItemsFormTable from "../table/orderItem/OrderItemsFormTable";
import {OrderItemReferenceValue} from "../../../interfaces/OrderItemInterfaces";
import Spinner from "../../atoms/Spinner";

interface OrderItemsFormListProps extends RouteProps {
  referenceType: OrderItemReferenceValue,
  setReferenceType: (referenceType: OrderItemReferenceValue) => void
}

const OrderItemsFormList: FunctionComponent<OrderItemsFormListProps> = ({
  referenceType,
  setReferenceType
}) => {
  const intl = useIntl()

  const initialSearchState: OrderSearchObject = useMemo(() => ({
    tourId: null,
    customerId: null,
    startDate: null,
    endDate: null,
    status: [OrderState.DRAFT, OrderState.PENDING, OrderState.HANDLED],
  }), [])

  const {searchObject, updateSearchField} = useSearchFilter<OrderSearchObject>(initialSearchState)

  const {
    data,
    page,
    setPage,
    pageSize,
    setPageSize,
    refresh,
    total,
    loading,
  } = usePaginatedData(
    referenceType === OrderItemReferenceValue.DESCRIPTION ? orderService.getOrdersPageWithReferenceDescription : orderService.getOrdersPageWithReferencePrice,
    searchObject,
    undefined,
    200,
  )

  useEffect(() => {
    setPage(0);
    refresh();
  }, [referenceType]);

  return (
    <div>
      <div>
        <FilterGroup className="d-flex flex-wrap z10">
          <FormAutocompleteSelectPageable
            id="customer"
            className="ms-2 w-25 epow-filter"
            label={intl.formatMessage({id: "order_customer"})}
            placeholder={intl.formatMessage({id: "customer_placeholder"})}
            onChange={(value) => updateSearchField("customerId", [value])}
            value={searchObject?.customerId}
            fetchData={(page, filter) => customerService.getCustomersPage(page, {
              ...filter,
              currentState: WorkflowStatesEnum.ACTIVE
            })}
            filterFieldName="fullName"
            filterFieldSearch="name"
            error={null}
            touched={null}
          />
          <FormAutocompleteSelectPageable
            id="tour"
            className="ms-2 w-25 epow-filter"
            label={intl.formatMessage({id: "order_tour"})}
            placeholder={intl.formatMessage({id: "tour_placeholder"})}
            onChange={(value) => updateSearchField("tourId", value)}
            value={searchObject.tourId}
            fetchData={(page, filter) => tourService.getToursPage(page, {
              ...filter,
              currentState: WorkflowStatesEnum.ACTIVE
            })}
            filterFieldName="fullName"
            filterFieldSearch="tourName"
            error={null}
            touched={null}
          />
          <DateFilter className="mb-3 ms-2" value={searchObject.startDate} onChangeDate={(value) => updateSearchField("startDate", value)} title={intl.formatMessage({id: "filter_from"})} />
          <DateFilter className="mb-3 ms-2" value={searchObject.endDate} onChangeDate={(value) => updateSearchField("endDate", value)} title={intl.formatMessage({id: "filter_to"})} />
          <div className="mb-3">
            <Button
              className="mx-2"
              onClick={() => {
                updateSearchField("customerId", null)
                updateSearchField("tourId", null)
                updateSearchField("startDate", "")
                updateSearchField("endDate", "")
              }}
              color={ColorType.SECONDARY}>
                <FormattedMessage id="header_menu_clear_search"/>
            </Button>
          </div>
        </FilterGroup>
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <OrderItemsFormTable referenceType={referenceType} setReferenceType={setReferenceType} orders={data} refresh={refresh} />
          <Pagination page={page} setPage={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} total={total}/>
        </div>
      )}
    </div>
  )
}

export default OrderItemsFormList;
